<template>
  <div class="mass-transfer">

    <div class="container-header">
      <div class="container-header__title">{{ i18n.tr_create_mass() }}</div>
    </div>

    <md-card class="container container_40 mass-transfer__card">
      <p class="mass-transfer__text-paragraph text_medium">{{ i18n.tr_mass_about() }}</p>
      <div class="mass-transfer__link" @click="isHowToOpened = true">{{ i18n.tr_how_to_create_csv() }}</div>
      <file-field class="mass-transfer__upload-input"
                  v-model="documents.transfers"
                  accept=".csv"
                  id="preissuance-field"
      />
      <template v-if="recipientNotFound">
        <p class="mass-transfer__errors">
          {{ i18n.tr_found_errors() }}:
          <span>{{ i18n.tr_current_recipient_not_found({recipient: recipientNotFound}) }}</span>
        </p>
      </template>
      <template v-if="transfers.length">
        <div class="mass-transfer__total-wrap">
          <p class="mass-transfer__total text_medium">
            {{ i18n.tr_total_amount() }}:
            <template v-for="(amount, asset, i) in totals.amounts">
              <template v-if="Object.keys(totals.amounts).length > 1"><br>{{ i+1 }}.</template>
              <span class="mass-transfer__amount">{{ roundCurrency(amount, asset) }}</span>
              <span class="mass-transfer__asset">{{ assetIcon(asset) }}</span>
            </template>
          </p>
          <p class="mass-transfer__total text_medium">
            {{ i18n.tr_total_source_fee() }}:
            <template v-for="(amount, asset, i) in totals.sourceFees">
              <template v-if="Object.keys(totals.sourceFees).length > 1"><br>{{ i+1 }}.</template>
              <span class="mass-transfer__amount">{{ roundCurrency(amount, asset) }}</span>
              <span class="mass-transfer__asset">{{ assetIcon(asset) }}</span>
            </template>
          </p>
          <p class="mass-transfer__total text_medium">
            {{ i18n.tr_total_recipient_fee() }}:
            <template v-for="(amount, asset, i) in totals.destinationFees">
              <template v-if="Object.keys(totals.destinationFees).length > 1"><br>{{ i+1 }}.</template>
              <span class="mass-transfer__amount">{{ roundCurrency(amount, asset) }}</span>
              <span class="mass-transfer__asset">{{ assetIcon(asset) }}</span>
            </template>
          </p>
        </div>
      </template>

      <template v-if="transfers.length">
        <md-table class="mass-transfer__receiver-table text_medium">
          <md-table-row>
            <md-table-head class="mass-transfer__table-cell">{{ i18n.lbl_status() }}</md-table-head>
            <md-table-head class="mass-transfer__table-cell">{{ i18n.lbl_email() }}</md-table-head>
            <md-table-head class="mass-transfer__table-cell">{{ i18n.lbl_amount() }}</md-table-head>
            <md-table-head class="mass-transfer__table-cell">
              {{ i18n.lbl_source_fees() }}<br>
              {{ i18n.lbl_fixed_percent() }}
            </md-table-head>
            <md-table-head class="mass-transfer__table-cell">
              {{ i18n.lbl_destination_fees() }}<br>
              {{ i18n.lbl_fixed_percent() }}
            </md-table-head>
          </md-table-row>
          <template v-for="transfer in transfers">
            <md-table-row>
              <md-table-cell class="mass-transfer__table-cell" :class="{ 'mass-transfer__table-cell--error':
                                       transfer.status && transfer.status !== 'Success'
                                    }"
              >
                {{ transfer.status || '--' }}
              </md-table-cell>
              <md-table-cell class="mass-transfer__table-cell">{{ transfer.email }}</md-table-cell>
              <md-table-cell class="mass-transfer__table-cell">
                {{ roundCurrency(transfer.amount, transfer.asset) }} {{ assetIcon(transfer.asset) }}
              </md-table-cell>
              <md-table-cell class="mass-transfer__table-cell">
                {{ roundCurrency(transfer.sourceFees.fixed, transfer.asset) }} / {{ roundCurrency(transfer.sourceFees.percent, transfer.asset) }}
                {{ assetIcon(transfer.sourceFees.feeAsset) }}
              </md-table-cell>
              <md-table-cell class="mass-transfer__table-cell">
                {{ roundCurrency(transfer.destinationFees.fixed, transfer.asset) }} / {{ roundCurrency(transfer.destinationFees.percent, transfer.asset) }}
                {{ assetIcon(transfer.destinationFees.feeAsset) }}
              </md-table-cell>
            </md-table-row>
          </template>
        </md-table>
      </template>

      <div class="btn-wrap space-between">
        <md-button class="btn md-primary"
                   :disabled="!transfers.length || isPending"
                   @click="submit"
        >{{ i18n.lbl_confirm_send() }}
        </md-button>
      </div>
    </md-card>

    <md-dialog :md-active.sync="isHowToOpened">
      <div class="text_large mass-transfer__dialog-title">{{ i18n.tr_about_csv() }}</div>
      <div>
        <p class="mass-transfer__dialog-text">{{ i18n.tr_mass_about_detailed() }}</p>
        <md-table class="mass-transfer__dialog-table text_medium">
          <md-table-row>
            <md-table-head class="mass-transfer__table-cell">{{ i18n.lbl_recipient_email_or_account() }}</md-table-head>
            <md-table-head class="mass-transfer__table-cell">{{ i18n.lbl_amount() }}</md-table-head>
            <md-table-head class="mass-transfer__table-cell">{{ i18n.lbl_asset() }}</md-table-head>
          </md-table-row>
          <md-table-row>
            <md-table-cell>alice@mail.com</md-table-cell>
            <md-table-cell>0.012</md-table-cell>
            <md-table-cell>BTC</md-table-cell>
          </md-table-row>
          <md-table-row>
            <md-table-cell>bob@mail.com</md-table-cell>
            <md-table-cell>1.541</md-table-cell>
            <md-table-cell>ETH</md-table-cell>
          </md-table-row>
          <md-table-row>
            <md-table-cell>john@mail.com</md-table-cell>
            <md-table-cell>0.998</md-table-cell>
            <md-table-cell>ETH</md-table-cell>
          </md-table-row>
        </md-table>

        <p class="mass-transfer__dialog-table text_medium">{{ i18n.tr_should_look_like() }}</p>

        <blockquote class="mass-transfer__csv-example">
          alice@mail.com,0.012,BTC <br>
          bob@mail.com,1.541,ETH <br>
          john@mail.com,0.998,ETH <br>
        </blockquote>

        <p class="mass-transfer__dialog-table text_medium">{{ i18n.tr_use_editor() }}</p>
      </div>
      <div class="btn-wrap margin-top">
        <md-button class="btn md-primary" @click="isHowToOpened = false">{{ i18n.lbl_got_it() }}</md-button>
      </div>
    </md-dialog>
  </div>
</template>

<script>
  import { FileHelper } from '@js/helpers/file.helper'
  import { ErrorHandler } from '@js/errors/error_handler'
  import { EventDispatcher } from '@js/events/event_dispatcher'
  import { Keypair } from 'tokend-js-sdk'

  import { accountsService } from '@api/accounts.service'
  import { transferService } from '@api/transfers.service'
  import { feeService } from '@api/fees.service'

  import { i18n } from '@js/i18n'

  import { PAYMENT_FEE_SUBTYPES } from '@const'

  import FileField from '@com/fields/FileField'
  import FormMixin from '@com/mixins/form.mixin'

  import { add } from '@utils/math.util'

  import {mapActions, mapGetters} from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import { errors, ErrorFactory, errorTypes } from '@js/errors/factory'
  import { CurrencyHelper } from '@js/helpers/currency.helper'
  import CurrencyRound from '@com/mixins/currency.round.mixin'

  export default {
    name: 'MassTransfersIndex',
    components: { FileField },
    mixins: [FormMixin, CurrencyRound],
    data: _ => ({
      documents: {
        transfers: null
      },
      transfers: [],
      recipientNotFound: '',
      i18n,
      isHowToOpened: false
    }),
    computed: {
      ...mapGetters([
        vuexTypes.accountBalances,
        vuexTypes.factorData
      ]),
      totals () {
        const amountsByAssets = {}
        const sourceFeesByAssets = {}
        const destinationFeesByAssets = {}

        this.transfers.forEach(transfer => {
          amountsByAssets[transfer.asset] = [...(amountsByAssets[transfer.asset] || []), transfer.amount]
          sourceFeesByAssets[transfer.sourceFees.feeAsset] = [
            ...(sourceFeesByAssets[transfer.sourceFees.feeAsset] || []),
            add(transfer.sourceFees.fixed, transfer.sourceFees.percent)
          ]
          destinationFeesByAssets[transfer.destinationFees.feeAsset] = [
            ...(destinationFeesByAssets[transfer.destinationFees.feeAsset] || []),
            add(transfer.destinationFees.fixed, transfer.destinationFees.percent)
          ]
        })

        const totalAmounts = {}
        const totalSourceFees = {}
        const totalDestinationFees = {}

        for (const [asset, amounts] of Object.entries(amountsByAssets)) {
          totalAmounts[asset] = amounts.reduce((total, amount) => add(total, amount), 0)
        }
        for (const [asset, amounts] of Object.entries(sourceFeesByAssets)) {
          totalSourceFees[asset] = amounts.reduce((total, amount) => add(total, amount), 0)
        }
        for (const [asset, amounts] of Object.entries(destinationFeesByAssets)) {
          totalDestinationFees[asset] = amounts.reduce((total, amount) => add(total, amount), 0)
        }

        return {
          amounts: totalAmounts,
          sourceFees: totalSourceFees,
          destinationFees: totalDestinationFees
        }
      }
    },
    methods: {
      ...mapActions({
        updateTwoFactorData: vuexTypes.SET_TWO_FACTOR_STATE
      }),
      async submit () {
        this.disable()

        try {
          await transferService.createMassTransfer(this.transfers.map(transfer => ({
            sourceBalanceId: this.accountBalances[transfer.asset].balance_id,
            destinationAccountId: transfer.accountId,
            amount: transfer.amount,
            feeData: {
              sourceFee: {
                maxPaymentFee: transfer.sourceFees.percent,
                fixedFee: transfer.sourceFees.fixed,
                feeAsset: transfer.sourceFees.feeAsset
              },
              destinationFee: {
                maxPaymentFee: transfer.destinationFees.percent,
                fixedFee: transfer.destinationFees.fixed,
                feeAsset: transfer.destinationFees.feeAsset
              }
            },
            sourcePaysForDest: false,
            subject: ''
          })))
          EventDispatcher.dispatchShowSuccessEvent(i18n.tr_successful())
        } catch (error) {
          if (error instanceof errors.TFAExceededCodeError) {
            EventDispatcher.dispatchShowErrorEvent(i18n.tfa_to_many_attempts())
          } else {
            const messages = ErrorHandler.deriveTxErrorMessages(error)
            if (!messages || !messages.length) {
              ErrorHandler.processUnexpected(error)
            } else {
              this.transfers.forEach((transfer, i) => {
                transfer.status = messages[i]
              })
              EventDispatcher.dispatchShowErrorEvent(i18n.tr_mass_payment_failed())
            }
          }
        }

        this.enable()
      },
      assetIcon (asset) {
        return CurrencyHelper.getCurrencyIcon(asset)
      },
      async parseFile () {
        if (!this.documents.transfers) return
        const objKeys = ['recipient', 'amount', 'asset']
        const extracted = await FileHelper.readFileAsText(this.documents.transfers.file)

        const parsed = extracted
          .replace(/\r\n/g, '\n')
          .replace(/\r/g, '\n')
          .replace(/\u0022/g, '')
          .split('\n')
          .filter(row => row)
          .map(row => row
            .split(',')
            .reduce((transfer, item, i) => ({
              ...transfer, [objKeys[i]]: item.trim()
            }), {})
          )
        await this.loadTransferDetails(parsed)
      },
      async loadTransferDetails (transfers) {
        this.disable()
        this.transfers = []
        this.recipientNotFound = ''
        try {
          for (const transfer of transfers) {
            if (Keypair.isValidPublicKey(transfer.recipient)) {
              transfer.email = await accountsService.loadEmailByAccountId(transfer.recipient)
              transfer.accountId = transfer.recipient
            } else {
              transfer.accountId = await accountsService.loadAccountIdByEmail(transfer.recipient)
              transfer.email = transfer.recipient
            }
            if (!transfer.email || !transfer.accountId) {
              this.recipientNotFound = transfer.recipient
              ErrorFactory.throwError(errorTypes.NotFoundError, { email: transfer.recipient })
            }

            transfer.sourceFees = await feeService.loadPaymentFeeByAmount(
              transfer.asset,
              transfer.amount
            )
            transfer.destinationFees = await feeService.loadPaymentFeeByAmount(
              transfer.asset,
              transfer.amount,
              transfer.accountId,
              PAYMENT_FEE_SUBTYPES.incoming
            )
            transfer.sourcePaysForDest = false
          }
          this.transfers = transfers
        } catch (e) {
          console.error(e)
          if (e instanceof errors.NotFoundError) {
            e.showBanner(i18n.tr_not_valid_file())
            this.enable()
            return
          }
          ErrorHandler.processUnexpected(e)
        }
        this.enable()
      }
    },
    watch: { 'documents.transfers': 'parseFile' }
  }
</script>

<style lang="scss">
  @import '~scss/variables';
  @import '~scss/mixins';

  .mass-transfer {

    &__card {
      width: 100%;
      max-width: 1200px;
    }

    &__link{
      color: $col-md-primary;
      margin: 12px 0;
      cursor: pointer;
    }

    &__upload-input {
      margin-bottom: 12px;
    }

    &__total-wrap {
      margin: 24px 0;
    }

    &__total {
      margin-bottom: 8px;
      color: $col-text-secondary;
    }

    &__amount,
    &__asset {
      color: $col-text;
    }

    &__receiver-table {
      padding: 18px;
      @include border(1px, 8px, $col-border)
    }

    &__dialog-title,
    &__dialog-text {
      margin-bottom: 16px;
    }

    &__dialog-table {
      margin: 12px 0;
      padding: 12px;
      @include border(0, 8px)
    }
  }


  .mass-transfer__table-cell {
    white-space: nowrap;

    .md-checkbox {
      margin: 0 !important;
    }

    &--error {
      color: $col-md-accent;
    }
  }

  .mass-transfer__table-cell--account-id {
    .md-table-cell-container {
      max-width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .mass-transfer__csv-example {
    border: 1px solid rgba($col-unfocused, .5);
    background: rgba($col-md-primary, .05);
    color: $col-md-accent;
    border-radius: 5px;
    padding: .5rem 1rem;
    margin-bottom: 1rem;
  }
</style>



// WEBPACK FOOTER //
// src/vue/app/massTransfers/MassTransfers.Make.vue